import { QueryClient } from "@tanstack/react-query";
import { ApiError } from "./client";
import type { Uuid } from "./types";

/**
 * Кэш запросов.
 *
 * Берётся на то и только на то, что «спросил у сервера — показал»: состав
 * сервера, права, роли, список приглашений, сессии. Всё это грузилось эффектом
 * в локальное состояние — с флагом `alive` против гонки, с `null` в роли
 * «загружается» и с ручным перезапросом после каждого действия.
 *
 * Чего сюда **не** переносится: ленты, счётчики непрочитанного и очередь
 * отправки. Они живут в сторе, потому что их меняет не ответ на запрос, а
 * событие из сокета, и правило «куда встанет черта» никакой кэш за нас не
 * решит. Кэш — про «что сервер ответил на этот вопрос», стор — про «что у нас
 * сейчас на экране»; смешивать их и значило бы завести два ответа на один
 * вопрос.
 *
 * Клиент один и лежит в модуле, а не собирается в дереве: его надо чистить
 * при выходе из той же точки, где чистятся сторы (`StoreProvider`).
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      /*
       * Отказ сервера повторять нечего.
       *
       * Умолчание — три повтора на любую неудачу, и на «прав нет» это три
       * одинаковых отказа подряд вместо одного. Повтор осмыслен там, где
       * не доехало: оборвалась сеть, узел моргнул. Отдельно 401: его
       * `api/client.ts` разбирает сам — обновляет доступ и повторяет
       * запрос, — и второй такой же повтор поверх ему только мешает.
       */
      retry: (count, error) => {
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
        return count < 1;
      },

      /*
       * Свежесть здесь приходит событиями из сокета, а не возвратом в окно.
       *
       * Перезапрос по фокусу дал бы залп запросов на каждое переключение
       * между окном и игрой — а в голосовом канале туда-обратно ходят
       * постоянно. Что устарело по-настоящему, помечается явно: состав
       * сервера — по событию о вступившем, права — после правки ролей.
       */
      refetchOnWindowFocus: false,

      /*
       * Полминуты «не спрашивать заново».
       *
       * Настройки сервера и канала открываются модальным окном, и закрыть
       * его, чтобы тут же открыть снова, — обычное дело. Без этого каждое
       * открытие заново тянуло бы состав и роли, которые не менялись.
       */
      staleTime: 30_000,
    },
  },
});

/**
 * Ключи запросов.
 *
 * Одним местом, а не строками по экранам: состав сервера читает оболочка, а
 * помечает устаревшим окно настроек — и разойтись им нельзя. Опечатка в
 * строке выглядит потом как «не обновилось», и искать её негде.
 *
 * Первым идёт вид, вторым — чьё: так `["rights"]` помечает разом права и
 * сервера, и канала, а это ровно то, что нужно после правки ролей.
 */
export const keys = {
  rail: () => ["rail"] as const,
  webhooks: (channelId: Uuid) => ["webhooks", channelId] as const,
  serverRights: (serverId: Uuid) => ["rights", "server", serverId] as const,
  channelRights: (channelId: Uuid) => ["rights", "channel", channelId] as const,
  members: (serverId: Uuid) => ["members", serverId] as const,
  roles: (serverId: Uuid) => ["roles", serverId] as const,
  /** Переопределения прав на канале: и для @все, и для ролей. */
  overrides: (channelId: Uuid) => ["overrides", channelId] as const,
  bans: (serverId: Uuid) => ["bans", serverId] as const,
  /** Посты форума. */
  posts: (channelId: Uuid) => ["posts", channelId] as const,
  /** АвтоМод сервера. */
  automod: (serverId: Uuid) => ["automod", serverId] as const,
  welcome: (serverId: Uuid) => ["welcome-screen", serverId] as const,
  followedThreads: (serverId: Uuid) => ["followed-threads", serverId] as const,
  /** Одна ветка — её имя для плашки ссылки на неё. */
  thread: (threadId: Uuid) => ["thread", threadId] as const,
  /** Кто в ветке — те, кто за ней следит. */
  threadMembers: (threadId: Uuid) => ["thread-members", threadId] as const,
  /** События сервера. */
  events: (serverId: Uuid) => ["events", serverId] as const,
  /** Закреплённые в канале. */
  pins: (channelId: Uuid) => ["pins", channelId] as const,
  /** Превью ссылки. */
  embed: (url: string) => ["embed", url] as const,
  /** Картинка карточки ссылки — ссылкой на объект в памяти. */
  embedImage: (image: string) => ["embed-image", image] as const,
  /** Поиск по каналу или серверу: `where` — номер того, где ищем. */
  search: (where: Uuid, query: string, filter: object = {}) => ["search", where, query, filter] as const,
  invites: (serverId: Uuid) => ["invites", serverId] as const,
  sounds: (serverId: Uuid) => ["sounds", serverId] as const,
  sessions: () => ["sessions"] as const,
  /** Текст вложения для предпросмотра прямо в ленте. */
  fileText: (fileId: Uuid) => ["file-text", fileId] as const,
  /** Есть ли в этой сборке нативный захват экрана. Ответ на приложение один. */
  nativeCapture: () => ["native-capture"] as const,
  /** Может ли оболочка взять звук приложения для показа (Linux). */
  appSound: () => ["app-sound"] as const,
  /** Приложения, у которых звук есть прямо сейчас. */
  soundApps: () => ["sound-apps"] as const,
  /** Где оболочка держит файл журнала. В браузере — нигде. */
  journalPath: () => ["journal-path"] as const,
  user: (userId: Uuid) => ["user", userId] as const,
  note: (userId: Uuid) => ["note", userId] as const,
  /** Общие с человеком серверы и друзья — в профиле. */
  mutual: (userId: Uuid) => ["mutual", userId] as const,
  /** Чем человек чаще всего отвечает: из этого собран ряд быстрых реакций. */
  frequentEmoji: () => ["emoji", "frequent"] as const,
  /** Избранные знаки. */
  favoriteEmoji: () => ["emoji", "favorites"] as const,
  /** Свои знаки сервера — для колонки серверов в панели эмодзи. */
  serverEmoji: (serverId: string) => ["emoji", "server", serverId] as const,
  /** «Входящие → Упоминания». */
  inbox: () => ["inbox"] as const,
  /** Сохранённые сообщения. */
  saved: () => ["saved"] as const,
  reminders: () => ["reminders"] as const,
};
